import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { PlugZap, Save, Trash2, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

const CAT_COLOR = {
  "Location & Caller Data": "#22D3EE", "Voice / SMS": "#A78BFA", "Mapping / GIS": "#10B981",
  "EMS Protocols": "#EF4444", "Digital Evidence": "#F59E0B", "Radio / Alerting": "#3B82F6",
  "Gunshot Detection": "#DC2626", "Caller Profiles": "#8B5CF6",
};

function TwilioTest() {
  const [st, setSt] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/e911/sms-status").then((r) => setSt(r.data)).catch(() => setSt(null));
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const r = await client.post("/e911/sms-test", { to });
      r.data.sent ? toast.success(`Test SMS sent to ${r.data.to} (${r.data.status})`) : toast.error(r.data.reason || "Send failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setBusy(false);
  };
  return (
    <div data-testid="twilio-test" className="border-t border-[#2C2D35] pt-2 mt-1 space-y-1.5">
      <div className="font-mono text-[9px] uppercase flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${st?.configured ? "bg-[#22C55E]" : "bg-[#EF4444]"}`} />
        <span className={st?.configured ? "text-[#22C55E]" : "text-[#EF4444]"} data-testid="twilio-status">
          {st?.configured ? `SMS gateway READY · from ${st.from_number} (${st.via})` : "SMS gateway NOT READY — save SID, token, from number and tick Enable"}
        </span>
        <button type="button" onClick={load} className="ml-auto text-[#22D3EE]">refresh</button>
      </div>
      <div className="flex gap-2">
        <input data-testid="twilio-test-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Your cell, e.g. +14105551234"
          className="flex-1 bg-[#141518] border border-[#2C2D35] px-2.5 py-1.5 text-[11px] font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm" />
        <button data-testid="twilio-test-send" type="button" onClick={send} disabled={busy || !st?.configured || to.replace(/\D/g, "").length < 10}
          className="px-3 py-1.5 text-[10px] font-head font-bold border border-[#A78BFA]/60 text-[#C4B5FD] rounded-sm disabled:opacity-40">SEND TEST SMS</button>
      </div>
    </div>
  );
}


function Card({ ig, onSaved }) {
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState(ig.credentials || {});
  const [enabled, setEnabled] = useState(ig.enabled);
  const [busy, setBusy] = useState(false);
  const color = CAT_COLOR[ig.category] || "#9CA3AF";

  const save = async () => {
    setBusy(true);
    try {
      await client.post(`/integrations/${ig.key}`, { enabled, credentials: creds });
      toast.success(`${ig.name} configuration saved`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const clear = async () => {
    try { await client.delete(`/integrations/${ig.key}`); toast(`${ig.name} credentials cleared`); onSaved(); }
    catch { toast.error("Clear failed"); }
  };

  return (
    <div data-testid={`integration-card-${ig.key}`} className="border border-[#2C2D35] bg-[#141518] rounded-sm flex flex-col">
      <div className="p-3">
        <div className="flex items-center gap-2">
          <span className="font-head font-bold text-sm text-white">{ig.name}</span>
          {ig.live
            ? <span className="font-mono text-[8px] font-bold px-1.5 py-0.5 bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40 rounded-sm uppercase" data-testid={`integration-live-${ig.key}`}>Live</span>
            : <span className="font-mono text-[8px] font-bold px-1.5 py-0.5 bg-[#6B7280]/15 text-[#9CA3AF] border border-[#6B7280]/40 rounded-sm uppercase">Stored only</span>}
          <span className={`ml-auto w-2 h-2 rounded-full ${ig.enabled ? "bg-[#22C55E]" : "bg-[#4B5563]"}`} title={ig.enabled ? "Enabled" : "Disabled"} />
        </div>
        <div className="font-mono text-[9px] uppercase mt-1" style={{ color }}>{ig.category}</div>
        <p className="text-[11px] text-[#9CA3AF] mt-1.5 leading-relaxed">{ig.desc}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${ig.configured ? "text-[#22C55E] border-[#22C55E]/40" : "text-[#6B7280] border-[#2C2D35]"}`}>
            {ig.configured ? "Credentials on file" : "Not configured"}
          </span>
          <button data-testid={`integration-configure-${ig.key}`} onClick={() => setOpen(!open)}
            className="ml-auto flex items-center gap-1 text-[10px] font-head font-semibold text-[#22D3EE] uppercase">
            Configure {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#2C2D35] p-3 bg-[#0d0e11] space-y-2">
          {ig.fields.map((f) => (
            <div key={f}>
              <label className="font-mono text-[9px] text-[#6B7280] uppercase">{f.replace(/_/g, " ")}</label>
              <input data-testid={`integration-field-${ig.key}-${f}`} type="text" value={creds[f] || ""}
                onChange={(e) => setCreds((p) => ({ ...p, [f]: e.target.value }))}
                placeholder={`Enter ${f.replace(/_/g, " ")}`}
                className="w-full bg-[#141518] border border-[#2C2D35] px-2.5 py-1.5 text-[11px] font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm mt-0.5" />
            </div>
          ))}
          <label className="flex items-center gap-2 text-[11px] font-mono text-[#9CA3AF] pt-1 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid={`integration-enable-${ig.key}`} />
            {ig.live ? "Enable this integration — LIVE: real SMS / lookups will be sent" : "Enable this integration (credentials stored — vendor link not yet wired)"}
          </label>
          {ig.key === "twilio" && <p className="font-mono text-[9px] text-[#6B7280]">From number must be your Twilio number in E.164 format, e.g. +14105551234. Save, then send a test text below.</p>}
          <div className="flex gap-2 pt-1">
            <button data-testid={`integration-save-${ig.key}`} onClick={save} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-head font-bold bg-[#3B82F6] text-white rounded-sm disabled:opacity-50"><Save size={11} /> SAVE</button>
            <button onClick={clear} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-head border border-[#2C2D35] text-[#EF4444] rounded-sm"><Trash2 size={11} /> CLEAR</button>
          </div>
          {ig.key === "twilio" && <TwilioTest />}
        </div>
      )}
    </div>
  );
}

export default function IntegrationCenter() {
  const [data, setData] = useState(null);
  const load = () => client.get("/integrations").then((r) => setData(r.data)).catch(() => setData({ integrations: [] }));
  useEffect(() => { load(); }, []);
  return (
    <Layout>
      <PageHeader title="Integration Center" sub="External vendor integrations · secure credential slots · LIVE = real vendor traffic" icon={PlugZap} />
      <div className="flex-1 overflow-y-auto p-5" data-testid="integration-center">
        {data?.banner && (
          <div className="flex items-center gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] font-mono text-[10px] px-3 py-2 mb-4 rounded-sm">
            <ShieldCheck size={13} /> {data.banner}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(data?.integrations || []).map((ig) => <Card key={ig.key} ig={ig} onSaved={load} />)}
        </div>
        {data && !data.integrations.length && <div className="font-mono text-xs text-[#6B7280]">Integration catalog unavailable (supervisor/admin only).</div>}
      </div>
    </Layout>
  );
}
